import React from "react";
import { notification, Progress } from "antd";
import {
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

/**
 * Display usage notification to user
 * @param {Object} usageData - Usage data from API
 * @param {string} featureType - Type of feature (product_analytics or brand_analytics)
 * @param {Object} userProfile - User profile data from Redux store
 */
export const showUsageNotification = (usageData, featureType, userProfile) => {
  // Don't show notification if limits are not enforced
  if (!usageData.is_limited) {
    return;
  }

  const feature =
    featureType === "product_analytics"
      ? usageData.usage?.product_analytics
      : usageData.usage?.brand_analytics;

  if (!feature) {
    return;
  }

  const { current_usage, limit, remaining, percentage_used } = feature;

  // Determine notification type based on usage
  let notificationType = "info";
  let icon = <InfoCircleOutlined className="tw-text-blue-500" />;
  let title = "Информация об использовании";
  let message = "";

  if (remaining === null || !limit) {
    // Unlimited access
    message = "У вас неограниченный доступ к этой функции.";
  } else if (remaining === 0) {
    // Limit reached
    notificationType = "error";
    icon = <CloseCircleOutlined className="tw-text-red-500" />;
    title = "Лимит исчерпан";
    message = `Вы достигли лимита использования (${current_usage}/${limit}). Обратитесь в техподдержку для повышения тарифа.`;
  } else if (percentage_used >= 80) {
    // Warning - close to limit
    notificationType = "warning";
    icon = <WarningOutlined className="tw-text-orange-500" />;
    title = "Приближение к лимиту";
    message = `Осталось ${remaining} из ${limit} запросов (${percentage_used.toFixed(
      1
    )}% использовано).`;
  } else {
    // Normal usage
    message = `Использовано ${current_usage} из ${limit} запросов. Осталось: ${remaining}.`;
  }

  // Create notification content with progress bar
  const description = (
    <div className="tw-space-y-3">
      <p className="tw-mb-3 tw-text-sm tw-text-gray-700">{message}</p>
      {limit && (
        <>
          <Progress
            percent={percentage_used}
            status={
              percentage_used >= 100
                ? "exception"
                : percentage_used >= 80
                ? "normal"
                : "active"
            }
            strokeColor={
              percentage_used >= 100
                ? "#ef4444"
                : percentage_used >= 80
                ? "#f97316"
                : "#22c55e"
            }
          />
          <div className="tw-mt-2">
            <span className="tw-text-xs tw-text-gray-600">
              <strong className="tw-font-semibold">Текущий тариф:</strong> {userProfile?.tariff_info?.name}
            </span>
          </div>
        </>
      )}
    </div>
  );

  notification.open({
    message: title,
    description: description,
    icon: icon,
    placement: "topRight",
    duration: 5,
  });
};

/**
 * Show error notification for usage limit exceeded
 * @param {Object} errorData - Error data from API
 */
export const showUsageLimitExceededNotification = (errorData) => {
  notification.error({
    message: "Превышен лимит запросов",
    description:
      errorData.message ||
      "Вы исчерпали лимит запросов. Обратитесь в техподдержку для повышения тарифа.",
    placement: "topRight",
    duration: 8,
  });
};

export default { showUsageNotification, showUsageLimitExceededNotification };
